import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';  // ← Add this
import { XMarkIcon, CameraIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const PhotoCaptureModal = ({
  show,
  onClose,
  onCapture,
  capturedPhoto,
  setCapturedPhoto
}) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  useEffect(() => {
    if (show) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [show]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraStream(stream);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    setIsCapturing(true);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      setCapturedPhoto(dataUrl);
      onCapture(file, dataUrl);
      setIsCapturing(false);
    }, 'image/jpeg', 0.8);
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    startCamera();
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedPhoto(e.target.result);
        onCapture(file, e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (!show) return null;

  // ↓ Wrap entire modal in a portal so it renders outside any stacking context
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }} 
    >
      {/* Backdrop — intercepts all clicks behind */}
      <div
        className="absolute inset-0 bg-black bg-opacity-75"
        onClick={onClose}  // optional: click outside to close
      />

      {/* Modal panel — above the backdrop */}
      <div className="relative bg-white rounded-lg max-w-2xl w-full" style={{ zIndex: 10000 }}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Capture Photo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {capturedPhoto ? (
            <div className="text-center">
              <img
                src={capturedPhoto}
                alt="Captured photo"
                className="mx-auto max-w-full h-auto rounded-lg shadow-lg"
              />
              <div className="mt-4 flex justify-center space-x-4">
                <button
                  onClick={retakePhoto}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <ArrowPathIcon className="h-4 w-4 mr-2" />
                  Retake Photo
                </button>
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Use This Photo
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              {cameraError ? (
                <div className="text-center py-8">
                  <p className="text-red-600 mb-4">{cameraError}</p>
                  <div className="space-y-4">
                    <button
                      onClick={startCamera}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <CameraIcon className="h-4 w-4 mr-2" />
                      Try Again
                    </button>
                    <div className="text-sm text-gray-600">Or upload a photo from your device:</div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <video
                    ref={videoRef}
                    autoPlay
                    muted
                    className="mx-auto max-w-full h-auto rounded-lg shadow-lg bg-gray-200"
                  />
                  <canvas ref={canvasRef} className="hidden" />
                  <div className="mt-4 flex justify-center space-x-4">
                    <button
                      onClick={capturePhoto}
                      disabled={isCapturing}
                      className="inline-flex items-center px-6 py-3 border border-transparent rounded-full shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                      <CameraIcon className="h-5 w-5 mr-2" />
                      {isCapturing ? 'Capturing...' : 'Capture Photo'}
                    </button>
                  </div>
                  <div className="mt-4 text-sm text-gray-600">Or upload a photo from your device:</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body  
  );
};

export default PhotoCaptureModal;